(() => {
  const API = "https://api.okendo.io/v1/stores";
  const MEDIA_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
    "video/mp4",
    "video/quicktime",
    "video/webm",
  ]);

  class ReviewForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector("dialog");
      this.form = this.querySelector("form");
      this.error = this.querySelector("[data-review-error]");
      this.success = this.querySelector("[data-review-success]");
      this.submitButton = this.querySelector('[type="submit"]');
      // The shared button wraps its label in a trim span; swap text there so the box keeps its height.
      this.submitLabel = this.submitButton.querySelector(".t-trim") ?? this.submitButton;
      this.fileInput = this.form.elements.media;
      this.attributesContainer = this.querySelector("[data-review-attributes]");
      this.attributesStatus = this.querySelector("[data-review-attributes-status]");
      this.attributesFallback = this.querySelector("[data-review-attributes-fallback]");
      this.attributeDefinitions = [];
      this.attributesReady = false;
      this.uploads = new Map();
      this.api = `${API}/${encodeURIComponent(this.dataset.subscriberId)}`;

      this.querySelectorAll("[data-review-close]").forEach((button) => {
        button.addEventListener("click", () => this.dialog.close());
      });
      this.dialog.addEventListener("click", (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        ) {
          this.dialog.close();
        }
      });
      this.fileInput.addEventListener("change", () => {
        this.uploads.clear();
        this.fileInput.setCustomValidity("");
        const files = Array.from(this.fileInput.files);
        if (
          files.length > 5 ||
          files.some(
            (file) =>
              !MEDIA_TYPES.has(file.type) || file.size > 30 * 1024 * 1024,
          )
        ) {
          this.fileInput.setCustomValidity(this.dataset.fileError);
          this.fileInput.reportValidity();
        }
        this.querySelector("[data-review-files]").textContent = files
          .map((file) => file.name)
          .join(", ");
        this.querySelector("[data-review-remove-files]").hidden = !files.length;
      });
      this.querySelector("[data-review-remove-files]").addEventListener(
        "click",
        () => {
          this.fileInput.value = "";
          this.fileInput.dispatchEvent(new Event("change"));
        },
      );
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      if (!this.attributesReady) this.loadAttributes();
    }

    async loadAttributes() {
      if (this.loadingAttributes) return;
      this.loadingAttributes = true;
      this.submitButton.disabled = true;
      this.attributesStatus.hidden = false;
      this.attributesStatus.textContent = this.dataset.attributesLoading;
      this.attributesFallback.hidden = true;
      try {
        const response = await fetch(
          `${this.api}/products/shopify-${encodeURIComponent(this.dataset.productId)}`,
        );
        if (!response.ok) throw new Error("Unable to load review questions");
        const { product } = await response.json();
        if (!product || (product.attributes !== undefined && !Array.isArray(product.attributes)))
          throw new Error("Missing review questions");
        const attributes = product.attributes ?? [];
        // Use Okendo's configuration instead of keeping a second question list.
        // New unsupported types use the full form rather than silently losing data.
        if (attributes.some((attribute) =>
          attribute.target !== "general" ||
          !["range", "centered-range"].includes(attribute.type)))
          throw new Error("Use the full form for these questions");
        this.attributeDefinitions = attributes;
        const fields = this.attributeDefinitions.map((attribute, index) => {
          const fieldset = document.createElement("fieldset");
          fieldset.className = "review-compose-attribute";
          const legend = document.createElement("legend");
          legend.textContent = attribute.title + (attribute.isOptional ? "" : " *");
          const options = document.createElement("div");
          options.className = "review-compose-attribute-options";
          for (let value = 1; value <= 5; value++) {
            const label = document.createElement("label");
            const input = document.createElement("input");
            input.type = "radio";
            input.name = `attribute-${index}`;
            input.value = String(value);
            input.required = !attribute.isOptional;
            const text = document.createElement("span");
            text.textContent = String(value);
            label.append(input, text);
            options.append(label);
          }
          const endpoints = document.createElement("div");
          endpoints.className = "review-compose-attribute-labels";
          for (const text of [attribute.minLabel, ...(attribute.midLabel ? [attribute.midLabel] : []), attribute.maxLabel]) {
            const label = document.createElement("span");
            label.textContent = text;
            endpoints.append(label);
          }
          fieldset.append(legend, options, endpoints);
          return fieldset;
        });
        this.attributesContainer.replaceChildren(...fields);
        this.attributesReady = true;
        this.attributesStatus.hidden = true;
      } catch {
        this.attributesStatus.textContent = this.dataset.attributesError;
        this.attributesFallback.hidden = false;
      } finally {
        this.loadingAttributes = false;
        this.submitButton.disabled = !this.attributesReady;
      }
    }

    async post(path, body) {
      const response = await fetch(`${this.api}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("Okendo request failed");
      return response.json();
    }

    async upload(file) {
      if (this.uploads.has(file)) return this.uploads.get(file);
      const type = file.type.startsWith("image/") ? "image" : "video";
      const { signedS3UploadUrl, streamId } = await this.post(
        "/utils/signed_s3_url",
        {
          contentType: file.type,
          usageId: `recorder-${type}`,
        },
      );
      if (!signedS3UploadUrl || !streamId)
        throw new Error("Missing upload URL");
      const response = await fetch(decodeURIComponent(signedS3UploadUrl), {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) throw new Error("Upload failed");
      const media = {
        type: file.type,
        mimetype: file.type,
        streamId,
        source: "uploaded",
      };
      this.uploads.set(file, media);
      return media;
    }

    async submit() {
      if (this.submitting || !this.attributesReady) return;
      for (const name of ["title", "body", "name", "email"]) {
        const field = this.form.elements[name];
        field.value = field.value.trim();
      }
      if (!this.form.reportValidity()) return;
      const data = new FormData(this.form);
      this.submitting = true;
      this.error.hidden = true;
      this.submitButton.disabled = true;
      this.form.querySelectorAll("input, textarea, button").forEach((field) => {
        field.disabled = true;
      });
      this.submitLabel.textContent = this.dataset.sending;
      this.form.setAttribute("aria-busy", "true");
      try {
        const media = [];
        for (const file of this.fileInput.files)
          media.push(await this.upload(file));
        const payload = {
          rating: Number(data.get("rating")),
          title: String(data.get("title")).trim(),
          body: String(data.get("body")).trim(),
          reviewer: { name: String(data.get("name")).trim(), attributes: [] },
          reviewerEmail: String(data.get("email")).trim(),
          attributesWithRating: this.attributeDefinitions.flatMap((attribute, index) => {
            const value = Number(data.get(`attribute-${index}`));
            if (!Number.isInteger(value) || value < 1 || value > 5) return [];
            return [{
              title: attribute.original?.title ?? attribute.title,
              value,
              type: attribute.type,
              isPrivate: Boolean(attribute.isPrivate),
            }];
          }),
          productAttributes: [],
          media,
        };
        const result = await this.post(
          `/products/shopify-${encodeURIComponent(this.dataset.productId)}/reviews`,
          payload,
        );
        if (!result.review?.reviewId)
          throw new Error("Missing review confirmation");
        this.form.hidden = true;
        this.success.hidden = false;
        this.form.reset();
        this.uploads.clear();
        if (this.dialog.open) this.success.focus();
      } catch {
        this.error.textContent = this.dataset.error;
        this.error.hidden = false;
      } finally {
        this.submitting = false;
        this.submitButton.disabled = false;
        this.form
          .querySelectorAll("input, textarea, button")
          .forEach((field) => {
            field.disabled = false;
          });
        this.submitLabel.textContent = this.dataset.submitLabel;
        this.form.removeAttribute("aria-busy");
      }
    }
  }

  if (!customElements.get("review-form"))
    customElements.define("review-form", ReviewForm);
})();
